import { inject, Injectable, OutputEmitterRef } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs';

import { deepEqual } from '@core/utils-legacy';
import type { InputOptionsState, OptionsQueryParams } from '@core/views-data';
import { getFilterOptionsId, InputOptionsActions, InputOptionsSelectors } from '@core/views-data';
import type {
  FilterPayload,
  ViewFilterInputDef,
  ViewFilterInputPayload,
  FilterControl,
} from '@core/views-types';

import type { InputTypeComponent } from '../advanced-filters/filter/types';
import { FiltersFormStateService } from '../services/filters-form-state.service';
import type { FilterValues } from './input-multiselect/input-multiselect.service';
import type { InputMultiSelectOptions } from './input-multiselect/types';
import type { InputSelectOptions } from './input-select/types';
import type { InputComponentContext } from './types';
import { FilterEvents } from './types';

@Injectable()
export class OptionsService {
  private readonly PAGE_SIZE = 30;
  private readonly VALIDATE_OPTIONS_EVENTS = [
    FilterEvents.InputBlur,
    FilterEvents.ChipRemove,
    FilterEvents.Clear,
  ];

  private store = inject(Store);
  private actions$ = inject(Actions);
  private filterStateService = inject(FiltersFormStateService);

  public loadOptions(
    context: InputComponentContext,
    filterValue: FilterPayload | null,
    queryParams?: { searchQuery?: string },
  ) {
    const inputOptionsId = this.getInputOptionsId(context);
    this.store.dispatch(
      InputOptionsActions.fetchOne({
        payload: {
          dataSourceId: context.dataSourceId,
          filterId: context.filterDef.id,
          inputId: context.inputDef.id,
          inputOptionsId,
          filterValue,
          queryParams: {
            limit: this.PAGE_SIZE,
            page: 1,
            searchQuery: queryParams?.searchQuery,
          },
        },
      }),
    );
  }

  public loadMoreOptions(
    context: InputComponentContext,
    filterValue: FilterPayload | null,
    queryParams: OptionsQueryParams,
  ) {
    const inputOptionsId = this.getInputOptionsId(context);
    this.store.dispatch(
      InputOptionsActions.fetchMore({
        payload: {
          dataSourceId: context.dataSourceId,
          filterId: context.filterDef.id,
          inputId: context.inputDef.id,
          inputOptionsId,
          filterValue,
          queryParams,
        },
      }),
    );
  }

  public validate(context: InputComponentContext, filterValue: FilterPayload) {
    const inputOptionsId = this.getInputOptionsId(context);
    this.store.dispatch(
      InputOptionsActions.validate({
        payload: {
          dataSourceId: context.dataSourceId,
          filterId: context.filterDef.id,
          inputId: context.inputDef.id,
          inputOptionsId,
          filterValue,
        },
      }),
    );
  }

  public validateOptions<
    InputComponent extends InputTypeComponent<any, unknown, any>,
    Options extends InputSelectOptions | InputMultiSelectOptions,
  >(
    context: InputComponentContext,
    filterControl: FilterControl,
    hasValueFn: (inputControl: InputComponent['inputControl']) => boolean,
  ) {
    const inputOptionsId = this.getInputOptionsId(context);
    const inputIndex = this.getInputIndex(context.inputs, context.inputDef.id);
    const inputControl = <InputComponent['inputControl']>(
      filterControl.controls.value.controls[inputIndex]
    );

    return context.filterEvents.pipe(
      filter(({ inputId, event }) => {
        const hasValue = hasValueFn(inputControl);
        const isDependency = context.inputDef.props.subscriptions.includes(inputId);
        const isValidateEvent = this.VALIDATE_OPTIONS_EVENTS.includes(event);
        return hasValue && isDependency && isValidateEvent;
      }),
      switchMap(() => {
        const filterValue = this.filterStateService.filterFormToViewValue(
          filterControl.getRawValue(),
        );
        this.validate(context, filterValue);
        return this.getValidation<Options>(inputOptionsId);
      }),
    );
  }

  public getOptions<T extends InputSelectOptions | InputMultiSelectOptions>(
    inputOptionsId: string,
  ) {
    return <Observable<InputOptionsState<T> | null>>(
      this.store.select(InputOptionsSelectors.byId(inputOptionsId))
    );
  }

  public getInputOptionsId(context: InputComponentContext) {
    return getFilterOptionsId(
      context.dataSourceId,
      context.filterDef.id,
      context.inputDef.id,
      context.uuid,
    );
  }

  public getInputIndex(inputs: ViewFilterInputDef[], inputId: string) {
    return inputs.findIndex(({ id }) => id === inputId);
  }

  public getSubscriptionsChanges(
    context: InputComponentContext,
    destroy$: Observable<void>,
  ): Observable<FilterValues> {
    const filterControl = this.filterStateService.getFilterControl(context.uuid)!;
    const selfIndex = this.getInputIndex(context.inputs, context.inputDef.id);
    const { subscriptions } = context.inputDef.props;
    const subscriptionsIndex = subscriptions.map(inputId =>
      context.inputs.findIndex(({ id }) => id === inputId),
    );

    const subscriptions$: Observable<unknown> = subscriptions.length
      ? combineLatest(
          subscriptionsIndex.map(index => {
            const inputControl = filterControl.controls.value.controls[index];
            return inputControl.valueChanges.pipe(startWith(inputControl.value));
          }),
        )
      : of(null);

    return subscriptions$.pipe(
      debounceTime(0),
      map(() => {
        const value = this.filterStateService.filterFormToViewValue(filterControl.getRawValue());
        return {
          fullValue: value,
          skipSelfValue: {
            ...value,
            inputs: value.inputs.map(
              (input, index): ViewFilterInputPayload =>
                index !== selfIndex ? input : { configId: input.configId, value: null },
            ),
          },
        };
      }),
      shareReplay(1),
      takeUntil(destroy$),
    );
  }

  public handleLoadMore(
    context: InputComponentContext,
    destroy$: Observable<void>,
    subscriptions$: Observable<FilterValues>,
    loadMore: OutputEmitterRef<OptionsQueryParams>,
  ) {
    subscriptions$
      .pipe(
        switchMap(filterValue =>
          outputToObservable(loadMore).pipe(
            distinctUntilChanged((a, b) => deepEqual(a, b)),
            map(params => ({ params, filterValue })),
          ),
        ),
        takeUntil(destroy$),
      )
      .subscribe(({ params, filterValue }) => {
        this.loadMoreOptions(context, filterValue.skipSelfValue, params);
      });
  }

  public handleLoadOptions(
    context: InputComponentContext,
    destroy$: Observable<void>,
    subscriptions$: Observable<FilterValues>,
    search$: OutputEmitterRef<string | null>,
  ) {
    combineLatest([subscriptions$, outputToObservable(search$).pipe(startWith(null))])
      .pipe(debounceTime(0), takeUntil(destroy$))
      .subscribe(([filterValues, query]) => {
        const filterValue = context.inputDef.props.subscriptions.length
          ? filterValues.skipSelfValue
          : null;
        this.loadOptions(context, filterValue, query ? { searchQuery: query } : undefined);
      });
  }

  private getValidation<T extends InputSelectOptions | InputMultiSelectOptions>(
    inputOptionsId: string,
  ) {
    return this.actions$.pipe(
      ofType(InputOptionsActions.validateSuccess),
      filter(({ payload }) => payload.data.id === inputOptionsId),
      map(({ payload }) => <T[]>payload.data.options),
    );
  }
}
